// src/app/pages/campus/CampusWrapper.tsx
import { useEffect, useState } from 'react';
import { PageTitle } from '../../../_metronic/layout/core';
import styleScss from './styles.module.scss';
import { useAuth } from '../../modules/auth';
import { LinearLoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import BackButton from '../../components/BackButton/BackButton';
import campusService, { Course, CourseStats, CourseFilters } from './services/campusService';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TablePagination,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
  AlertTitle,
  Button,
  Menu
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { initValue } from '../../util/Index';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';

const CampusPage = () => {
  const { isAdminRole, isCounselorCandidateRole, isCounselorEmployerRole } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [stats, setStats] = useState<CourseStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');

  // Pagination
  const [total, setTotal] = useState<number>(0);
  const [filter, setFilter] = useState<CourseFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    startDateFrom: '',
    startDateTo: '',
    minStudents: '',
    maxStudents: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const hasAccess = () => {
    return isAdminRole() || isCounselorCandidateRole() || isCounselorEmployerRole();
  };

  const loadCourses = async (filtersParams: any = {}) => {
    if (!hasAccess()) {
      console.warn('Usuario sin permisos para acceder al modulo Campus de formacion');
      return;
    }

    setLoading(true);
    try {
      const data = await campusService.getCourses(filtersParams);
      setCourses(data.courses);
      setStats(data.stats);
      setTotal(data.courses.length);
    } catch (error: any) {
      if (error.response) {
        console.error(error.response.data.error);
        setOpen(true);
        setAlert(false);
        setAlertMsg(error.response.data.error || 'Error al cargar los cursos');
        setTimeout(() => setOpen(false), 2000);
      } else {
        console.error('Ocurrio un error procesando la solicitud.');
        setOpen(true);
        setAlert(false);
        setAlertMsg('Ocurrio un error procesando la solicitud.');
        setTimeout(() => setOpen(false), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  // Carga inicial
  useEffect(() => {
    loadCourses();
  }, []);

  // Efecto para validar filtros de rango antes de ejecutar la busqueda
  useEffect(() => {
    const hasValidDateRange =
      (filter.startDateFrom && filter.startDateTo) ||
      (!filter.startDateFrom && !filter.startDateTo);

    const hasValidStudentsRange =
      (filter.minStudents && filter.maxStudents) ||
      (!filter.minStudents && !filter.maxStudents);

    const minNum = filter.minStudents ? parseInt(filter.minStudents) : 0;
    const maxNum = filter.maxStudents ? parseInt(filter.maxStudents) : 0;
    const hasValidNumbers =
      (!filter.minStudents || minNum >= 0) &&
      (!filter.maxStudents || maxNum >= 0) &&
      (!filter.minStudents || !filter.maxStudents || minNum <= maxNum);

    if (hasValidDateRange && hasValidStudentsRange && hasValidNumbers) {
      loadCourses(filter);
    }
  }, [filter.search, filter.status, filter.startDateFrom, filter.startDateTo, filter.minStudents, filter.maxStudents]);

  const handleChangePage = (newPage: any) => {
    const newPageNumber = parseInt(newPage) + 1;
    const f = {
      ...filter,
      page: newPageNumber,
      limit: filter.limit
    };
    setPage(newPage);
    setFilter(f);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const f = {
      ...filter,
      page: 1,
      limit: parseInt(event.target.value, 10)
    };
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setFilter(f);
  };

  const handleFirstPageButtonClick = () => {
    handleChangePage(0);
  };

  const handleLastPageButtonClick = () => {
    handleChangePage(Math.ceil(total / rowsPerPage) - 1);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClearFilters = () => {
    setFilter({
      page: 1,
      limit: 10,
      search: '',
      status: '',
      startDateFrom: '',
      startDateTo: '',
      minStudents: '',
      maxStudents: ''
    });
    setPage(0);
  };

  // MenuAction
  const MenuAction = (props: any) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { data } = props;

    const handleClick = (e: any) => {
      setAnchorEl(e.currentTarget);
    };

    const handleCloseMenu = () => {
      setAnchorEl(null);
    };

    const handleVerAsistentes = (id: any) => {
      return `/consejero/campus/participantes/${id}`;
    };

    return (
      <>
        <Button onClick={handleClick}>
          <i className='bi bi-three-dots' style={{ fontSize: '20px' }}></i>
        </Button>

        <Menu
          id='card-actions-menu'
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={handleCloseMenu} key={`verAsistentes${data.id}`}>
            <Link to={handleVerAsistentes(data.id)} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
              <PeopleIcon />
              &nbsp; Ver asistentes
            </Link>
          </MenuItem>
        </Menu>
      </>
    );
  };

  if (!hasAccess()) {
    return (
      <div className="text-center py-5">
        <h3>Acceso denegado</h3>
        <p>No tienes permisos para acceder a este modulo.</p>
      </div>
    );
  }

  // Paginacion local: cortar los cursos segun la pagina actual
  const paginatedCourses = courses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <PageTitle breadcrumbs={[]}>Formacion asistida (Virtual)</PageTitle>

      <div className='p-4 shadow-4 rounded-3 mt-15 row' style={{ backgroundColor: '#FFFF' }}>
        <div className='col-md-1 row mb-4' style={{ alignItems: 'center' }}>
          <div className='input-group d-flex' style={{ width: '100%', alignItems: 'center' }}>
            <div style={{ padding: '10px 0px 0px 15px' }}>
              <div className='col-xs-2 col-sm-1 col-md-1'
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                <BackButton />
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className='col-md-11 row mb-4' style={{ alignItems: 'center' }}>
          <div className='col-sm-12'>
            <div className='input-group d-flex' style={{ width: '100%', alignItems: 'center', padding: '0 15px', flexWrap: 'wrap', gap: '8px' }}>
              {/* Buscar curso */}
              <div className='col-lg-3 col-xs-12 col-sm-6 col-md-3' style={{ padding: '10px 8px 1px 0px' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={initValue(filter.search)}
                  onChange={(e) => {
                    setFilter((prev: CourseFilters) => ({ ...prev, page: 1, search: e.target.value }));
                    setPage(0);
                  }}
                  label="Buscar curso"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <IconButton edge="end">
                        <i className='bi bi-search' style={{ fontSize: '20px' }} />
                      </IconButton>
                    ),
                  }}
                  sx={{
                    fontFamily: 'Poppins',
                    '& .MuiOutlinedInput-root': {
                      height: '38px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      fontFamily: 'Poppins',
                      fontSize: '14.5px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                    }
                  }}
                />
              </div>

              {/* Estado */}
              <div className='col-lg-2 col-xs-12 col-sm-4 col-md-2' style={{ padding: '10px 8px 1px 0px' }}>
                <FormControl fullWidth size="small">
                  <InputLabel id='status-filter' style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
                    Estado
                  </InputLabel>
                  <Select
                    labelId='status-filter'
                    label='Estado'
                    value={initValue(filter.status)}
                    style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}
                    onChange={(e) => {
                      setFilter((prev: CourseFilters) => ({ ...prev, page: 1, status: e.target.value }));
                      setPage(0);
                    }}
                  >
                    <MenuItem value=''>Todos</MenuItem>
                    <MenuItem value='En curso'>En curso</MenuItem>
                    <MenuItem value='Finalizado'>Finalizado</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Fecha inicio desde */}
              <div className='col-lg-2 col-xs-12 col-sm-4 col-md-2' style={{ padding: '10px 8px 1px 0px' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha inicio desde"
                  value={initValue(filter.startDateFrom)}
                  onChange={(e) => {
                    setFilter((prev: CourseFilters) => ({
                      ...prev,
                      page: 1,
                      startDateFrom: e.target.value
                    }));
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    fontFamily: 'Poppins',
                    '& .MuiOutlinedInput-root': {
                      height: '38px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      fontFamily: 'Poppins',
                      fontSize: '14.5px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                    }
                  }}
                />
              </div>

              {/* Fecha inicio hasta */}
              <div className='col-lg-2 col-xs-12 col-sm-4 col-md-2' style={{ padding: '10px 8px 1px 0px' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha inicio hasta"
                  value={initValue(filter.startDateTo)}
                  onChange={(e) => {
                    setFilter((prev: CourseFilters) => ({
                      ...prev,
                      page: 1,
                      startDateTo: e.target.value
                    }));
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    fontFamily: 'Poppins',
                    '& .MuiOutlinedInput-root': {
                      height: '38px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      fontFamily: 'Poppins',
                      fontSize: '14.5px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                    }
                  }}
                />
              </div>

              {/* Inscritos desde */}
              <div className='col-lg-2 col-xs-12 col-sm-4 col-md-2' style={{ padding: '10px 8px 1px 0px' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Inscritos desde"
                  value={initValue(filter.minStudents)}
                  onChange={(e) => {
                    setFilter((prev: CourseFilters) => ({
                      ...prev,
                      page: 1,
                      minStudents: e.target.value
                    }));
                    setPage(0);
                  }}
                  InputProps={{ inputProps: { min: 0 } }}
                  sx={{
                    fontFamily: 'Poppins',
                    '& .MuiOutlinedInput-root': {
                      height: '38px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      fontFamily: 'Poppins',
                      fontSize: '14.5px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                    }
                  }}
                />
              </div>

              {/* Inscritos hasta */}
              <div className='col-lg-2 col-xs-12 col-sm-4 col-md-2' style={{ padding: '10px 8px 1px 0px' }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Inscritos hasta"
                  value={initValue(filter.maxStudents)}
                  onChange={(e) => {
                    setFilter((prev: CourseFilters) => ({
                      ...prev,
                      page: 1,
                      maxStudents: e.target.value
                    }));
                    setPage(0);
                  }}
                  InputProps={{ inputProps: { min: 0 } }}
                  sx={{
                    fontFamily: 'Poppins',
                    '& .MuiOutlinedInput-root': {
                      height: '38px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      fontFamily: 'Poppins',
                      fontSize: '14.5px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                    }
                  }}
                />
              </div>

              {/* Limpiar filtros */}
              <div className='col-lg-1 col-xs-12 col-sm-2 col-md-1' style={{ padding: '10px 8px 1px 0px' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  sx={{
                    height: '38px',
                    textTransform: 'none',
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <i className='bi bi-eraser me-1'></i>
                  Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && <LinearLoadingSpinner isLoading={loading} />}

        {/* Tabla de cursos */}
        {!loading && (
          <div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
            <Stack padding={2}>
              <TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
                <Table style={{ overflowY: 'visible' }}>
                  <TableHead className={`${styleScss['th-custom']}`}>
                    <TableRow>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                        <h4>Curso</h4>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                        <h4>Inscritos</h4>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                        <h4>Aprobados</h4>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                        <h4>Fecha Inicio</h4>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                        <h4>Fecha Fin</h4>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                        <h4>Estado</h4>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}>
                        <h4>Acciones</h4>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  {!loading && (
                    <TableBody>
                      {paginatedCourses.map((course: any, i: number) => (
                        <TableRow key={i} id={i.toString()}>
                          <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', overflowWrap: 'break-word', wordBreak: 'break-all', textAlign: 'center' }}>
                            <Tooltip title={course.fullname} placement='left' arrow>
                              <p style={{ margin: 0 }}>
                                <strong>{course.fullname}</strong>
                                <br />
                                <span style={{ fontSize: '12px', color: '#6c757d' }}>{course.shortname}</span>
                              </p>
                            </Tooltip>
                          </TableCell>
                          <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                            {course.total_students}
                          </TableCell>
                          <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                            <span className='text-success fw-semibold'>{course.approved_students}</span>
                            {course.total_students > 0 && (
                              <span className='text-muted ms-1'>
                                ({Math.round((course.approved_students / course.total_students) * 100)}%)
                              </span>
                            )}
                          </TableCell>
                          <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                            {course.startdate ? dayjs(course.startdate).format("DD/MM/YYYY") : '-'}
                          </TableCell>
                          <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                            {course.enddate ? dayjs(course.enddate).format("DD/MM/YYYY") : '-'}
                          </TableCell>
                          <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                            <span className={`badge ${course.status === 'En curso' ? 'bg-success' : course.status === 'Finalizado' ? 'bg-secondary' : 'bg-warning'}`}>
                              {course.status}
                            </span>
                          </TableCell>
                          <TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
                            <MenuAction data={{ id: course.id }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}

                  {courses.length === 0 && (
                    <tfoot>
                      <TableRow>
                        <TableCell colSpan={7}>
                          <p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
                            No se encontraron cursos
                          </p>
                        </TableCell>
                      </TableRow>
                    </tfoot>
                  )}
                </Table>

                <div style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    component="div"
                    style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => handleChangePage(newPage)}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por pagina:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    ActionsComponent={({ onPageChange, page: number, count, rowsPerPage }) => (
                      <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'Poppins', fontSize: '14.5px' }}>
                        <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
                          <FirstPageIcon />
                        </IconButton>
                        <IconButton onClick={(event) => onPageChange(event, page - 1)} disabled={page === 0} aria-label="previous page">
                          <ChevronLeftIcon />
                        </IconButton>
                        <IconButton onClick={(event) => onPageChange(event, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
                          <ChevronRightIcon />
                        </IconButton>
                        <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
                          <LastPageIcon />
                        </IconButton>
                      </div>
                    )}
                  />
                </div>
              </TableContainer>
            </Stack>
          </div>
        )}
      </div>

      {/* Snackbar de notificaciones */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          variant={'filled'}
          severity={alert ? 'success' : 'error'}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {alert ? <AlertTitle>Exito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
          {alertMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

const CampusWrapper = () => {
  return <CampusPage />;
};

export { CampusWrapper };